import { useEffect, useMemo, useState } from "react";
import { ArrowLeft, ArrowUpRight, Calendar, MapPin, Search, Trophy, Users, X } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";

import Layout from "../../../components/Layout";
import api, { formatApiError } from "../../../lib/api";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";

export default function Ricerca() {
  const navigate = useNavigate();
  const [comps, setComps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [status, setStatus] = useState("all"); // all | sent | draft

  useEffect(() => {
    api.get("/competitions")
      .then((r) => setComps(r.data))
      .catch((err) => toast.error(formatApiError(err)))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const qLower = q.trim().toLowerCase();
    return comps.filter((c) => {
      if (qLower) {
        const respStr = [c.responsabile_name, ...(c.responsabile_names || [])].filter(Boolean).join(" ");
        const hay = `${c.name} ${c.location} ${respStr}`.toLowerCase();
        if (!hay.includes(qLower)) return false;
      }
      if (from && c.date < from) return false;
      if (to && c.date > to) return false;
      if (status === "sent" && !c.sent) return false;
      if (status === "draft" && c.sent) return false;
      return true;
    });
  }, [comps, q, from, to, status]);

  const reset = () => { setQ(""); setFrom(""); setTo(""); setStatus("all"); };

  return (
    <Layout>
      <Link to="/admin" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 mb-6" data-testid="back-to-admin">
        <ArrowLeft className="h-4 w-4" /> Torna al pannello
      </Link>

      <div className="mb-8 flex items-center gap-3">
        <div className="h-10 w-10 rounded-xl bg-blue-50 text-blue-700 flex items-center justify-center">
          <Search className="h-5 w-5" />
        </div>
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-blue-700 font-bold">Tools Admin</p>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight mt-1">Filtri & Ricerca Competizioni</h1>
        </div>
      </div>

      <div className="bg-white rounded-2xl ring-1 ring-slate-200 p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
          <div className="md:col-span-5">
            <Label className="text-sm font-semibold text-slate-700">Cerca per nome, luogo, responsabile</Label>
            <div className="relative mt-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
              <Input
                placeholder="Es. Trofeo, Roma, Mario…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="h-11 pl-9"
                data-testid="search-input"
              />
            </div>
          </div>
          <div className="md:col-span-2">
            <Label className="text-sm font-semibold text-slate-700">Dal</Label>
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="mt-2 h-11" data-testid="filter-from" />
          </div>
          <div className="md:col-span-2">
            <Label className="text-sm font-semibold text-slate-700">Al</Label>
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="mt-2 h-11" data-testid="filter-to" />
          </div>
          <div className="md:col-span-2">
            <Label className="text-sm font-semibold text-slate-700">Stato</Label>
            <div className="mt-2 inline-flex rounded-lg bg-slate-100 p-1 w-full" role="tablist">
              {[
                { v: "all", l: "Tutte" },
                { v: "sent", l: "Inviate" },
                { v: "draft", l: "Bozza" },
              ].map((opt) => (
                <button
                  key={opt.v}
                  type="button"
                  onClick={() => setStatus(opt.v)}
                  className={`flex-1 px-2 py-2 text-xs font-semibold rounded-md transition ${status === opt.v ? "bg-white text-blue-700 shadow-sm" : "text-slate-600 hover:text-slate-900"}`}
                  data-testid={`filter-status-${opt.v}`}
                >
                  {opt.l}
                </button>
              ))}
            </div>
          </div>
          <div className="md:col-span-1">
            <Button variant="outline" onClick={reset} className="w-full h-11" data-testid="reset-filters">
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
        <div className="mt-4 text-sm text-slate-500" data-testid="result-count">
          {filtered.length} risultat{filtered.length === 1 ? "o" : "i"} su {comps.length} competizion{comps.length === 1 ? "e" : "i"}
        </div>
      </div>

      {loading ? (
        <div className="p-10 text-center text-slate-500 text-sm">Caricamento…</div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-2xl ring-1 ring-slate-200 p-10 text-center text-slate-500 text-sm">
          Nessuna competizione corrisponde ai filtri.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {filtered.map((c) => (
            <button
              key={c.id}
              onClick={() => navigate(c.sent ? `/admin/risultati/${c.id}` : `/admin/competizioni`)}
              className="group bg-white text-left rounded-2xl ring-1 ring-slate-200 hover:ring-blue-300 hover:shadow-md hover:-translate-y-0.5 transition-all p-6 flex flex-col gap-3"
              data-testid={`comp-result-${c.id}`}
            >
              <div className="flex items-start justify-between">
                <div className="h-10 w-10 rounded-lg bg-amber-50 text-amber-700 flex items-center justify-center">
                  <Trophy className="h-5 w-5" />
                </div>
                <ArrowUpRight className="h-5 w-5 text-slate-300 group-hover:text-slate-700 transition" />
              </div>
              <h3 className="font-display text-lg font-semibold text-slate-900">{c.name}</h3>
              <div className="text-sm text-slate-600 flex items-center gap-2"><MapPin className="h-4 w-4 text-slate-400" /> {c.location}</div>
              <div className="text-sm text-slate-600 flex items-center gap-2"><Calendar className="h-4 w-4 text-slate-400" /> {new Date(c.date).toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" })}</div>
              <div className="flex items-center justify-between mt-1">
                <span className="text-xs text-slate-500 inline-flex items-center gap-1.5"><Users className="h-3.5 w-3.5" /> {c.athletes?.length || 0} atleti · {c.judges?.length || 0} giudici</span>
                {c.sent ? (
                  <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Inviata</Badge>
                ) : (
                  <Badge variant="outline" className="border-slate-300 text-slate-600">Bozza</Badge>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </Layout>
  );
}
